import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { addMonths } from '@/utils/dateUtils';

/**
 * Minor learner parental-consent gate (ACC-352).
 *
 * Holds the login-time consent status and drives the gate:
 * - within 1 month of `consentPendingSince` the modal is dismissable and a
 *   dismiss "pauses" the gate for the rest of the session;
 * - after 1 month the gate is no longer dismissable and blocks the portal
 *   until consent is submitted.
 */
export const useMinorConsentStore = defineStore('minorConsent', () => {
  // Populated from GET /v2/api/learner-consent/status after login.
  const required = ref(false);
  const policyId = ref(null);
  const consentPendingSince = ref(null);

  // Soft-dismiss flag: true once the learner closes the modal within the grace
  // window. Session-scoped — reset on clear/login.
  const dismissedThisSession = ref(false);

  // View-As only (ACC-399 #1616): true once an admin observing a learner closes
  // the gate. Lets the admin dismiss even a past-grace (hard-block) modal so they
  // aren't trapped, WITHOUT weakening the real-learner block — it is only ever set
  // via dismissForProxy() from the View-As dismiss path, and reset on clear().
  const proxyDismissed = ref(false);

  // True once the 1-month grace window since `consentPendingSince` has elapsed,
  // at which point the gate becomes a hard block (no dismiss).
  const thresholdSurpassed = computed(() => {
    if (!required.value) return false;

    const deadline = addMonths(consentPendingSince.value, 1);
    return deadline !== null && Date.now() >= deadline.getTime();
  });

  // The modal may be dismissed only within the grace window.
  const dismissable = computed(() => !thresholdSurpassed.value);

  // Blocked while consent is required and the learner has neither submitted nor
  // (within the grace window) dismissed this session.
  const blocksPortal = computed(() => {
    if (!required.value) return false;
    if (proxyDismissed.value) return false;

    return thresholdSurpassed.value || !dismissedThisSession.value;
  });

  // Set the gate from a GET /v2/api/learner-consent/status response.
  function setStatus(status = {}) {
    required.value = Boolean(status?.isMinor && !status?.consentGiven);
    policyId.value = status?.policy?.id ?? null;
    consentPendingSince.value = status?.consentPendingSince ?? null;
  }

  // Soft dismiss — only honored within the grace window.
  function dismissForSession() {
    if (!thresholdSurpassed.value) {
      dismissedThisSession.value = true;
    }
  }

  // View-As dismiss — always honored (see proxyDismissed). Never call this for a
  // real learner; the grace/hard-block rules must hold for them.
  function dismissForProxy() {
    proxyDismissed.value = true;
  }

  function clear() {
    required.value = false;
    policyId.value = null;
    consentPendingSince.value = null;
    dismissedThisSession.value = false;
    proxyDismissed.value = false;
  }

  return {
    required,
    policyId,
    consentPendingSince,
    dismissedThisSession,
    proxyDismissed,
    thresholdSurpassed,
    dismissable,
    blocksPortal,
    setStatus,
    dismissForSession,
    dismissForProxy,
    clear
  };
});
